import { PrimitiveType, } from "@buildwithflux/models";
import { vec2 } from "../../../common";
import { TraceCorners } from "../../libs/traceCorners";
/** Returns an absolute-coordinates overly-approximated bounding box of that primitive useful for building BSPs */
export function approxAABoundingBox(primitive) {
    switch (primitive.type) {
        case PrimitiveType.Circle:
            return circleApproxBoundingBox(primitive);
        case PrimitiveType.Oblong:
            return oblongApproxBoundingBox(primitive);
        case PrimitiveType.Rectangle:
            return rectangleApproxBoundingBox(primitive);
        case PrimitiveType.RoundedRectangle:
            return roundedRectangleApproxBoundingBox(primitive);
        case PrimitiveType.Line:
            return lineApproxBoundingBox(primitive);
        case PrimitiveType.LineArc:
            return arcApproxBoundingBox(primitive);
        case PrimitiveType.Union:
            return unionApproxBoundingBox(primitive);
        case PrimitiveType.Difference:
            return differenceApproxBoundingBox(primitive);
        case PrimitiveType.ApproxPolygon:
            return approxPolygonApproxBoundingBox(primitive);
        case PrimitiveType.Expansion:
            return expansionApproxBoundingBox(primitive);
    }
}
function circleApproxBoundingBox(primitive) {
    return transformApproxBoundingBox({ minX: -primitive.radius, minY: -primitive.radius, maxX: primitive.radius, maxY: primitive.radius }, primitive.transform);
}
function oblongApproxBoundingBox(primitive) {
    return transformApproxBoundingBox({
        minX: -primitive.width / 2,
        minY: -primitive.height / 2,
        maxX: primitive.width / 2,
        maxY: primitive.height / 2,
    }, primitive.transform);
}
function rectangleApproxBoundingBox(primitive) {
    return transformApproxBoundingBox({
        minX: -primitive.width / 2,
        minY: -primitive.height / 2,
        maxX: primitive.width / 2,
        maxY: primitive.height / 2,
    }, primitive.transform);
}
function roundedRectangleApproxBoundingBox(primitive) {
    return transformApproxBoundingBox({
        minX: -primitive.width / 2,
        minY: -primitive.height / 2,
        maxX: primitive.width / 2,
        maxY: primitive.height / 2,
    }, primitive.transform);
}
function lineApproxBoundingBox(primitive) {
    const minPointX = Math.min(primitive.startX, primitive.endX);
    const minPointY = Math.min(primitive.startY, primitive.endY);
    const maxPointX = Math.max(primitive.startX, primitive.endX);
    const maxPointY = Math.max(primitive.startY, primitive.endY);
    return transformApproxBoundingBox({
        minX: minPointX - primitive.thickness / 2,
        minY: minPointY - primitive.thickness / 2,
        maxX: maxPointX + primitive.thickness / 2,
        maxY: maxPointY + primitive.thickness / 2,
    }, primitive.transform);
}
function arcApproxBoundingBox(primitive) {
    const { maxX, maxY, minX, minY } = TraceCorners.shapeBounds(primitive);
    return transformApproxBoundingBox({
        minX: minX - primitive.thickness / 2,
        minY: minY - primitive.thickness / 2,
        maxX: maxX + primitive.thickness / 2,
        maxY: maxY + primitive.thickness / 2,
    }, primitive.transform);
}
function unionApproxBoundingBox(primitive) {
    const bbA = approxAABoundingBox(primitive.primitiveA);
    const bbB = approxAABoundingBox(primitive.primitiveB);
    return transformApproxBoundingBox(mergeApproxBoundingBoxes([bbA, bbB]), primitive.transform);
}
function differenceApproxBoundingBox(primitive) {
    // TODO:
    // Since we are removing the primitiveB, it can't be bigger than primitiveA.
    // This is, though, an approximation, as the final result could be smaller.
    return transformApproxBoundingBox(approxAABoundingBox(primitive.primitiveA), primitive.transform);
}
function approxPolygonApproxBoundingBox(primitive) {
    const genesisPoint = primitive.points.at(0);
    if (!genesisPoint) {
        // TODO: Is this the correct behavior?
        return transformApproxBoundingBox({ minX: 0, minY: 0, maxX: 0, maxY: 0 }, primitive.transform);
    }
    let minPointX = genesisPoint.x;
    let minPointY = genesisPoint.y;
    let maxPointX = genesisPoint.x;
    let maxPointY = genesisPoint.y;
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    primitive.points.forEach((p) => {
        minPointX = Math.min(minPointX, p.x);
        minPointY = Math.min(minPointY, p.y);
        maxPointX = Math.max(maxPointX, p.x);
        maxPointY = Math.max(maxPointY, p.y);
    });
    return transformApproxBoundingBox({ minX: minPointX, minY: minPointY, maxX: maxPointX, maxY: maxPointY }, primitive.transform);
}
function expansionApproxBoundingBox(primitive) {
    const bb = approxAABoundingBox(primitive.primitive);
    return transformApproxBoundingBox({
        minX: bb.minX - primitive.expansion,
        minY: bb.minY - primitive.expansion,
        maxX: bb.maxX + primitive.expansion,
        maxY: bb.maxY + primitive.expansion,
    }, primitive.transform);
}
function transformApproxBoundingBox(boundingBox, transform) {
    const p1 = vec2.applyMatrix3({ x: boundingBox.minX, y: boundingBox.minY }, transform);
    let minX = p1.x;
    let minY = p1.y;
    let maxX = p1.x;
    let maxY = p1.y;
    const p2 = vec2.applyMatrix3({ x: boundingBox.minX, y: boundingBox.maxY }, transform);
    minX = Math.min(minX, p2.x);
    minY = Math.min(minY, p2.y);
    maxX = Math.max(maxX, p2.x);
    maxY = Math.max(maxY, p2.y);
    const p3 = vec2.applyMatrix3({ x: boundingBox.maxX, y: boundingBox.minY }, transform);
    minX = Math.min(minX, p3.x);
    minY = Math.min(minY, p3.y);
    maxX = Math.max(maxX, p3.x);
    maxY = Math.max(maxY, p3.y);
    const p4 = vec2.applyMatrix3({ x: boundingBox.maxX, y: boundingBox.maxY }, transform);
    minX = Math.min(minX, p4.x);
    minY = Math.min(minY, p4.y);
    maxX = Math.max(maxX, p4.x);
    maxY = Math.max(maxY, p4.y);
    return { minX, minY, maxX, maxY };
}
/** WARNING: Please check that the input array has at least one element */
export function mergeApproxBoundingBoxes(boundingBoxes) {
    // NOTE: This spread could break if there are a lot of bounding boxes
    const minX = Math.min(...boundingBoxes.map((bb) => bb.minX));
    const minY = Math.min(...boundingBoxes.map((bb) => bb.minY));
    const maxX = Math.max(...boundingBoxes.map((bb) => bb.maxX));
    const maxY = Math.max(...boundingBoxes.map((bb) => bb.maxY));
    return { minX, minY, maxX, maxY };
}
// Will return 0 in case of overlap
export function boundingBoxDistance(bcA, bcB) {
    // https://stackoverflow.com/a/65107290
    const uX = Math.max(0, bcA.minX - bcB.maxX);
    const uY = Math.max(0, bcA.minY - bcB.maxY);
    const u = { x: uX, y: uY };
    const vX = Math.max(0, bcB.minX - bcA.maxX);
    const vY = Math.max(0, bcB.minY - bcA.maxY);
    const v = { x: vX, y: vY };
    return Math.sqrt(vec2.len(u) ** 2 + vec2.len(v) ** 2);
}
export function boundingBoxToPolygon(bb) {
    return [
        { x: bb.minX, y: bb.minY },
        { x: bb.maxX, y: bb.minY },
        { x: bb.maxX, y: bb.maxY },
        { x: bb.minX, y: bb.maxY },
    ];
}
//# sourceMappingURL=approxAABoundingBox.js.map